import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronDownIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

export default function HeroSection() {
  return (
    <section className="relative bg-gradient-to-br from-slate-50 via-white to-orange-50 pt-24 pb-20 px-6 md:px-20 overflow-hidden">
      {/* Enhanced background elements */}
      <div className="absolute top-1/4 right-1/3 w-[500px] h-[500px] bg-gradient-to-r from-orange-100 to-amber-100 rounded-full blur-3xl opacity-30 -z-10"></div>
      <div className="absolute bottom-1/4 left-1/4 w-[300px] h-[300px] bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full blur-2xl opacity-20 -z-10"></div>
      
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-5 -z-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23000000' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>

      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row items-center justify-between gap-16">
          {/* Left: Enhanced text + CTA */}
          <div className="w-full md:w-1/2 text-center md:text-left space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm border border-orange-200 rounded-full text-sm font-medium text-orange-700 shadow-sm">
              <span className="w-2 h-2 bg-orange-500 rounded-full mr-2 animate-pulse"></span>
              Trusted by 1000+ families
            </div>

            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                <span className="text-slate-800">Simplifying</span>
                <br />
                <span className="bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
                  Indian Traditions
                </span>
                <br />
                <span className="text-slate-700">in the USA</span>
              </h1>
              
              <p className="text-xl text-slate-600 leading-relaxed max-w-lg">
                Connect with verified priests, decorators, caterers, and traditional service providers for your special occasions.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
              <Link
                to="/vendors"
                className="group bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold flex items-center justify-center gap-2 hover:from-orange-600 hover:to-orange-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
              >
                Explore Vendors 
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
              <Link
                to="/become-a-vendor"
                className="group border-2 border-slate-300 text-slate-700 px-8 py-4 rounded-full font-semibold flex items-center justify-center gap-2 hover:border-orange-500 hover:text-orange-600 hover:bg-orange-50 transition-all duration-200 bg-white/70 backdrop-blur-sm"
              >
                Become a Vendor
                <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-200" />
              </Link>
            </div>

            {/* Enhanced trust indicators */}
            <div className="flex flex-col sm:flex-row items-center justify-center md:justify-start gap-6 pt-4">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
                <span className="text-slate-600 font-medium">4.9 out of 5</span>
              </div>
              <div className="h-4 w-px bg-slate-300 hidden sm:block"></div>
              <span className="text-slate-600">200+ verified reviews</span>
            </div>

            {/* Professional trust badge */}
            <div className="flex items-center justify-center md:justify-start gap-4 pt-2">
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Background verified vendors
              </div>
              <div className="flex items-center gap-2 text-slate-500 text-sm">
                <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Secure booking platform
              </div>
            </div>
          </div>

          {/* Right: Enhanced Visual */}
          <div className="w-full md:w-1/2 flex justify-center items-center">
            <div className="relative">
              {/* Enhanced glow effect behind image */}
              <div className="absolute inset-0 bg-gradient-to-r from-orange-200 to-amber-200 rounded-3xl blur-2xl opacity-30 scale-110"></div>
              
              {/* Image container with professional styling */}
              <div className="relative bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-2xl border border-white/50">
                <img
                  src="/assets/event-graphic.png"
                  alt="Event service icons"
                  className="w-full max-w-lg drop-shadow-lg"
                />
              </div>
              
              {/* Floating elements for visual interest */}
              <div className="absolute -top-4 -right-4 w-8 h-8 bg-gradient-to-r from-orange-400 to-pink-400 rounded-full opacity-60 animate-pulse"></div>
              <div className="absolute -bottom-6 -left-6 w-6 h-6 bg-gradient-to-r from-blue-400 to-indigo-400 rounded-full opacity-60 animate-pulse delay-1000"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced scroll-down indicator */}
      <a
        href="#how-it-works"
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 group"
      >
        <div className="flex flex-col items-center gap-2">
          <span className="text-sm text-slate-500 font-medium group-hover:text-slate-700 transition-colors duration-200">
            Scroll to learn more
          </span>
          <div className="p-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg border border-white/50 group-hover:shadow-xl transition-all duration-200">
            <ChevronDownIcon className="w-6 h-6 text-slate-600 animate-bounce group-hover:text-slate-800" />
          </div>
        </div>
      </a>
    </section>
  );
}